/**
 * Landing — EduKampus marketing home at edukampus.com/
 *
 * Restored to the original bold dark-navy aesthetic (Space Grotesk display,
 * inter body, blue-gradient hero accent, "on a single platform." headline)
 * with an immersive 3D layer on top:
 *   - Floating CSS-3D orbs in the hero (framer-motion breathing + rotation)
 *   - Scroll-based parallax on those orbs (each one drifts at a different depth)
 *   - Lenis smooth-scroll for the whole page
 *   - Terracotta-blue scroll-progress bar pinned at the top
 *   - Fade-up-on-view animations on module cards + pricing tiers + FAQ
 */
import { useEffect, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { motion, useScroll, useTransform } from "framer-motion";
import Lenis from "lenis";
import {
  Accordion, AccordionContent, AccordionItem, AccordionTrigger,
} from "@/components/ui/accordion";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription,
} from "@/components/ui/dialog";
import { useAuth } from "@/context/AuthContext";
import ScrollProgress from "@/components/ScrollProgress";
import {
  ArrowRight, ArrowUpRight, Sparkles, ShieldCheck, Zap, Building2,
  UserSquare2, CalendarCheck2, Receipt, BookOpen, Users, Trophy,
  BellRing, LayoutGrid, GraduationCap, ChevronRight,
  MessageCircle, Phone, Mail, Twitter, Instagram, Youtube,
} from "lucide-react";

// ============================================================================
// Book-a-demo dialog — WhatsApp / Call / Email channels
// ============================================================================
// Primary contact details. One-tap actions below open the phone dialer,
// WhatsApp chat, mail client, or the respective social page directly.
const DEMO_CONTACT = {
  phone_intl: "+919035601902",   // opens dialer
  whatsapp_intl: "919035601902", // wa.me path (no +)
  email: "edukampus2025@gmail.com",
};
const SOCIAL_LINKS = {
  x:         "https://x.com/sashikunjaedu",
  instagram: "https://www.instagram.com/sashikunjaeducational/",
  youtube:   "https://youtube.com/@sashikunjaeducational?si=W7YurSLHSpQPpi3f",
};

// One-tap icon action tile — same shell used for both contact & social rows.
function IconLink({ href, external = false, icon: Icon, label, color, testid }) {
  return (
    <a
      href={href}
      target={external ? "_blank" : undefined}
      rel={external ? "noopener noreferrer" : undefined}
      data-testid={testid}
      aria-label={label}
      className="group flex flex-col items-center justify-center gap-2 rounded-xl border border-white/10 bg-white/[0.02] px-3 py-4 hover:border-white/25 hover:bg-white/[0.05] transition-all hover:-translate-y-0.5"
    >
      <div
        className="h-12 w-12 rounded-full grid place-items-center transition-transform group-hover:scale-110"
        style={{ background: `${color}22`, color }}
      >
        <Icon className="h-6 w-6" />
      </div>
      <div className="text-xs font-medium text-white/80 group-hover:text-white text-center leading-tight">{label}</div>
    </a>
  );
}

function BookDemoDialog({ open, onOpenChange }) {
  const waText = encodeURIComponent("Hi EduKampus team, I'd like to see a demo for my school.");

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="sm:max-w-md bg-[#0a0a10] text-white border-white/10 max-h-[92vh] overflow-y-auto" data-testid="book-demo-dialog">
        <DialogHeader>
          <div className="text-[10px] font-mono uppercase tracking-[0.3em] text-blue-400 mb-2">Talk to us</div>
          <DialogTitle className="font-display font-extrabold text-2xl tracking-tight text-white">Book a demo</DialogTitle>
          <DialogDescription className="text-white/60 text-sm mt-1">
            Reach out through any channel below — we usually respond within a few hours.
          </DialogDescription>
        </DialogHeader>

        {/* One-tap contact icons */}
        <div className="grid grid-cols-3 gap-3 mt-4">
          <IconLink
            href={`tel:${DEMO_CONTACT.phone_intl}`}
            icon={Phone}
            label="Call"
            color="#3b82f6"
            testid="demo-call"
          />
          <IconLink
            href={`https://wa.me/${DEMO_CONTACT.whatsapp_intl}?text=${waText}`}
            external
            icon={MessageCircle}
            label="WhatsApp"
            color="#10b981"
            testid="demo-whatsapp"
          />
          <IconLink
            href={`mailto:${DEMO_CONTACT.email}`}
            icon={Mail}
            label="Email"
            color="#a78bfa"
            testid="demo-email"
          />
        </div>

        {/* Stay Connected — social pages */}
        <div className="mt-8 pt-6 border-t border-white/10">
          <div className="text-[10px] font-mono uppercase tracking-[0.3em] text-blue-400 mb-2">Stay connected</div>
          <div className="font-display font-extrabold text-xl tracking-tight text-white">Follow us on social</div>
          <p className="text-white/60 text-sm mt-1">
            Product updates, feature releases, tutorials, and announcements.
          </p>
          <div className="grid grid-cols-3 gap-3 mt-4">
            <IconLink
              href={SOCIAL_LINKS.x}
              external
              icon={Twitter}
              label="X (Twitter)"
              color="#e2e8f0"
              testid="social-x"
            />
            <IconLink
              href={SOCIAL_LINKS.instagram}
              external
              icon={Instagram}
              label="Instagram"
              color="#ec4899"
              testid="social-instagram"
            />
            <IconLink
              href={SOCIAL_LINKS.youtube}
              external
              icon={Youtube}
              label="YouTube"
              color="#ef4444"
              testid="social-youtube"
            />
          </div>
        </div>
      </DialogContent>
    </Dialog>
  );
}

// ============================================================================
// Nav (fixed, blurred, dark)
// ============================================================================
function Nav({ onBookDemo }) {
  return (
    <nav className="fixed top-0 inset-x-0 z-40 border-b border-white/10 backdrop-blur-xl bg-[#050510]/70">
      <div className="max-w-7xl mx-auto px-6 h-16 flex items-center gap-6">
        <Link to="/" className="flex items-center gap-2 font-display font-extrabold text-white text-lg tracking-tight">
          <div className="h-8 w-8 rounded-lg bg-gradient-to-br from-blue-500 to-blue-700 grid place-items-center">
            <GraduationCap className="h-4 w-4 text-white" />
          </div>
          EduKampus
        </Link>
        <div className="hidden md:flex items-center gap-6 ml-6 text-sm text-white/70">
          <a href="#modules" className="hover:text-white transition-colors">Modules</a>
          <a href="#how-it-works" className="hover:text-white transition-colors">How it works</a>
          <a href="#faq" className="hover:text-white transition-colors">FAQ</a>
          <Link to="/campuses" className="hover:text-white transition-colors">Campus directory</Link>
        </div>
        <div className="ml-auto flex items-center gap-2">
          <Link to="/login" className="text-sm text-white/70 hover:text-white px-3 py-2" data-testid="landing-signin-link">Sign in</Link>
          <button onClick={onBookDemo} className="hidden sm:inline-flex items-center gap-1.5 h-9 px-4 rounded-full bg-white text-[#050510] font-semibold text-sm hover:bg-white/90 transition-colors" data-testid="landing-book-demo-nav">
            Book a demo <ArrowRight className="h-3.5 w-3.5" />
          </button>
        </div>
      </div>
    </nav>
  );
}

// ============================================================================
// 3D Hero orb — layered breathing + rotation + scroll parallax
// ============================================================================
// Each orb carries a `depth` (0-1): higher = closer to viewer = drifts faster.
function ParallaxOrb({ orb, scrollY }) {
  const yDrift = useTransform(scrollY, [0, 800], [0, -220 * orb.depth]);
  const xShift = useTransform(scrollY, [0, 800], [0, orb.xDrift]);
  const rotate = useTransform(scrollY, [0, 800], [0, orb.depth * 22]);
  return (
    <motion.div
      aria-hidden="true"
      style={{
        position: "absolute", left: orb.x, top: orb.y,
        width: orb.size, height: orb.size,
        y: yDrift, x: xShift, rotate,
        willChange: "transform",
      }}
    >
      <motion.div
        initial={{ opacity: 0, scale: 0.6 }}
        animate={{
          opacity: 1, scale: 1,
          y: [0, -22, 0],
          rotateX: [0, 12, 0],
          rotateY: [0, -16, 0],
        }}
        transition={{
          opacity: { duration: 1.0, delay: orb.delay },
          scale:   { duration: 1.0, delay: orb.delay, ease: [0.16, 1, 0.3, 1] },
          y:       { duration: orb.dur, repeat: Infinity, ease: "easeInOut", delay: orb.delay },
          rotateX: { duration: orb.dur, repeat: Infinity, ease: "easeInOut", delay: orb.delay },
          rotateY: { duration: orb.dur * 1.35, repeat: Infinity, ease: "easeInOut", delay: orb.delay },
        }}
        style={{
          width: "100%", height: "100%",
          borderRadius: "44% 56% 42% 58% / 48% 42% 58% 52%",
          background: orb.gradient,
          boxShadow: `0 40px 100px ${orb.shadow}, inset -18px -22px 60px rgba(0,0,0,0.35)`,
          transformStyle: "preserve-3d",
          filter: "blur(0.4px)",
        }}
      />
    </motion.div>
  );
}

function HeroOrbs() {
  const { scrollY } = useScroll();
  const orbs = [
    { size: 320, x: "62%", y: "6%",  dur: 11, delay: 0.0, depth: 0.9,
      gradient: "radial-gradient(circle at 30% 30%, #6b8dff 0%, #2c4bce 55%, #0a1a5c 100%)",
      shadow: "rgba(59,130,246,0.35)", xDrift: -50 },
    { size: 220, x: "80%", y: "48%", dur: 13, delay: 0.4, depth: 0.65,
      gradient: "radial-gradient(circle at 30% 30%, #9db4ff 0%, #4066e2 60%, #1a2a7a 100%)",
      shadow: "rgba(59,130,246,0.25)", xDrift: 40 },
    { size: 160, x: "48%", y: "68%", dur: 9,  delay: 0.8, depth: 0.5,
      gradient: "radial-gradient(circle at 30% 30%, #b5c8ff 0%, #6b8dff 55%, #26449e 100%)",
      shadow: "rgba(107,141,255,0.28)", xDrift: -30 },
    { size: 110, x: "70%", y: "82%", dur: 10, delay: 1.2, depth: 0.35,
      gradient: "radial-gradient(circle at 30% 30%, #c9d6ff 0%, #7dbbff 50%, #2f6bcc 100%)",
      shadow: "rgba(125,187,255,0.25)", xDrift: 60 },
  ];
  return (
    <div className="absolute inset-0 pointer-events-none" style={{ perspective: "1400px" }}>
      {orbs.map((o, i) => <ParallaxOrb key={i} orb={o} scrollY={scrollY} />)}
      {/* Soft radial vignette so the orbs feel embedded in space */}
      <div className="absolute inset-0" style={{ background: "radial-gradient(ellipse at 70% 40%, transparent 0%, rgba(5,5,16,0.55) 65%, rgba(5,5,16,0.85) 100%)" }} />
    </div>
  );
}

// ============================================================================
// Hero
// ============================================================================
function Hero({ onBookDemo }) {
  return (
    <section className="relative overflow-hidden bg-[#050510] text-white pt-32 pb-24 sm:pt-40 sm:pb-32 min-h-[680px]">
      {/* Ambient background: radial glows + subtle grid */}
      <div className="absolute inset-0 pointer-events-none">
        <div className="absolute -top-40 -left-40 h-[520px] w-[520px] rounded-full bg-blue-600/25 blur-3xl" />
        <div className="absolute -bottom-40 -right-40 h-[520px] w-[520px] rounded-full bg-indigo-500/20 blur-3xl" />
        <div className="absolute inset-0 grid-pattern opacity-[0.06]" />
      </div>

      {/* 3D orbs layer */}
      <HeroOrbs />

      {/* Content sits above the orbs on the left */}
      <div className="relative max-w-7xl mx-auto px-6">
        <motion.div initial={{ opacity: 0, y: 12 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.6 }}
          className="inline-flex items-center gap-2 rounded-full border border-white/15 bg-white/[0.04] px-3 py-1 text-[11px] font-mono uppercase tracking-[0.2em] text-white/80 mb-8">
          <Sparkles className="h-3 w-3 text-blue-400" />
          The Education Operating System · v2026
        </motion.div>

        <motion.h1 initial={{ opacity: 0, y: 18 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.8, delay: 0.05 }}
          className="font-display font-extrabold text-[clamp(2.75rem,7vw,6rem)] leading-[0.9] tracking-[-0.03em] max-w-5xl">
          Run your entire school
          <br />
          <span className="bg-clip-text text-transparent bg-gradient-to-r from-blue-400 via-sky-300 to-white">
            on a single platform.
          </span>
        </motion.h1>

        <motion.p initial={{ opacity: 0, y: 12 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.7, delay: 0.15 }}
          className="mt-8 text-white/70 text-lg sm:text-xl leading-relaxed max-w-2xl">
          Admissions, attendance, fees, exams, timetable, infrastructure, notices — every
          module a modern school needs, wired together and ready on day one. Multi-tenant,
          multi-campus, mobile-friendly.
        </motion.p>

        <motion.div initial={{ opacity: 0, y: 12 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.7, delay: 0.22 }}
          className="mt-10 flex flex-col sm:flex-row gap-3">
          <button
            onClick={onBookDemo}
            className="inline-flex items-center justify-center gap-2 h-12 px-6 rounded-full bg-white text-[#050510] font-semibold hover:bg-white/90 transition-colors group"
            data-testid="landing-hero-book-demo"
          >
            Book a demo
            <ArrowRight className="h-4 w-4 transition-transform group-hover:translate-x-0.5" />
          </button>
        </motion.div>

        {/* Live stats strip */}
        <motion.div initial={{ opacity: 0, y: 14 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.7, delay: 0.32 }}
          className="mt-14 grid grid-cols-2 sm:grid-cols-4 gap-6 max-w-3xl">
          {[
            { v: "17", l: "Live campuses" },
            { v: "3", l: "Institutions" },
            { v: "12+", l: "Modules" },
            { v: "24/7", l: "Cloud uptime" },
          ].map((s) => (
            <div key={s.l} className="border-l-2 border-blue-500/60 pl-4">
              <div className="font-display font-extrabold text-3xl sm:text-4xl">{s.v}</div>
              <div className="text-[10px] uppercase tracking-[0.2em] text-white/50 mt-1">{s.l}</div>
            </div>
          ))}
        </motion.div>
      </div>
    </section>
  );
}

// ============================================================================
// Trust bar
// ============================================================================
function TrustBar() {
  const schools = ["St. Peter's School", "US Academy", "St. Xaviers International"];
  return (
    <section className="border-y border-white/10 bg-[#050510]/95 py-8">
      <div className="max-w-7xl mx-auto px-6 flex flex-col md:flex-row items-center gap-6">
        <div className="text-[10px] font-mono uppercase tracking-[0.3em] text-white/50 md:pr-6 md:border-r md:border-white/10">
          Trusted by ambitious schools
        </div>
        <div className="flex flex-wrap items-center justify-center gap-x-8 gap-y-3 text-white/60 text-sm font-display font-semibold">
          {schools.map((s) => <span key={s} className="hover:text-white transition-colors">{s}</span>)}
        </div>
      </div>
    </section>
  );
}

// ============================================================================
// Modules
// ============================================================================
const MODULES = [
  { i: UserSquare2,     t: "Admissions",     d: "Application → offer → enrolment, with document uploads, seat holding, and fee-linked confirmation." },
  { i: CalendarCheck2,  t: "Attendance",     d: "Daily attendance for students & staff, class-wise summaries, parent SMS on absences." },
  { i: Receipt,         t: "Fees & Finance", d: "Structures, invoices, receipts, online payments, ledger, expense tracking, refunds." },
  { i: BookOpen,        t: "Curriculum",     d: "Classes, sections, subjects, syllabus, homework, gradebook, timetable." },
  { i: Trophy,          t: "Exams",          d: "Exam structure, session scheduling, automated seat mapping, results, report cards." },
  { i: Building2,       t: "Infrastructure", d: "Buildings, rooms, facilities, assets, maintenance tickets, compliance documents." },
  { i: Users,           t: "Staff & HR",     d: "Teacher & staff profiles, role-based access, attendance, payroll-ready exports." },
  { i: BellRing,        t: "Notices & Chat", d: "School-wide notices, class-scoped messages, parent chat, delivery receipts." },
];

function Modules() {
  return (
    <section id="modules" className="relative bg-[#0a0a10] text-white py-24 sm:py-32">
      <div className="max-w-7xl mx-auto px-6">
        <div className="max-w-3xl">
          <div className="text-[10px] font-mono uppercase tracking-[0.3em] text-blue-400 mb-4">
            <LayoutGrid className="h-3 w-3 inline -mt-0.5 mr-1.5" /> The stack
          </div>
          <h2 className="font-display font-extrabold text-4xl sm:text-5xl tracking-[-0.02em] leading-[0.95]">
            Everything a school runs on. In one place.
          </h2>
          <p className="mt-5 text-white/60 text-lg">
            No more juggling 6 spreadsheets, 3 WhatsApp groups, and 2 accountants. Every
            module below is live, hardened, and mobile-friendly out of the box.
          </p>
        </div>

        <div className="mt-14 grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4">
          {MODULES.map(({ i: Icon, t, d }, idx) => (
            <motion.div
              key={t}
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true, margin: "-60px" }}
              transition={{ duration: 0.5, delay: (idx % 4) * 0.08, ease: [0.16, 1, 0.3, 1] }}
              className="group relative rounded-2xl border border-white/10 bg-white/[0.02] p-6 hover:border-blue-500/40 hover:bg-white/[0.05] transition-all hover:-translate-y-0.5"
              data-testid={`module-card-${idx}`}
              style={{ transformStyle: "preserve-3d" }}
            >
              <div className="h-11 w-11 rounded-xl bg-gradient-to-br from-blue-500/25 to-indigo-500/10 border border-blue-500/30 grid place-items-center mb-5">
                <Icon className="h-5 w-5 text-blue-300" />
              </div>
              <div className="font-display font-bold text-lg tracking-tight">{t}</div>
              <div className="text-sm text-white/60 mt-2 leading-relaxed">{d}</div>
            </motion.div>
          ))}
        </div>
      </div>
    </section>
  );
}

// ============================================================================
// How it works
// ============================================================================
function HowItWorks() {
  const steps = [
    { n: "01", t: "Add your institutions & campuses", d: "One tenant, unlimited institutions, unlimited campuses. Bulk-onboard hundreds via CSV in 30 seconds." },
    { n: "02", t: "Invite your team & families",       d: "Roles (principal, HOD, teacher, accountant, parent) come pre-configured. Google sign-in or email — you pick." },
    { n: "03", t: "Every module works from day one",   d: "No 3-month implementation. Attendance, fees, exams, notices — all live the moment you save your first campus." },
  ];
  return (
    <section id="how-it-works" className="bg-[#050510] text-white py-24 sm:py-32 border-y border-white/10">
      <div className="max-w-7xl mx-auto px-6">
        <div className="max-w-3xl">
          <div className="text-[10px] font-mono uppercase tracking-[0.3em] text-blue-400 mb-4">
            <Zap className="h-3 w-3 inline -mt-0.5 mr-1.5" /> Zero-to-running
          </div>
          <h2 className="font-display font-extrabold text-4xl sm:text-5xl tracking-[-0.02em] leading-[0.95]">
            From signed contract to live portal — <span className="text-blue-400">under an hour.</span>
          </h2>
        </div>

        <div className="mt-14 grid grid-cols-1 md:grid-cols-3 gap-4">
          {steps.map((s, i) => (
            <motion.div
              key={s.n}
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true, margin: "-60px" }}
              transition={{ duration: 0.5, delay: i * 0.1, ease: [0.16, 1, 0.3, 1] }}
              className="rounded-2xl border border-white/10 bg-white/[0.02] p-8 relative" data-testid={`step-${i}`}
            >
              <div className="text-blue-500 font-mono text-xs tracking-[0.3em]">STEP {s.n}</div>
              <div className="mt-3 font-display font-bold text-2xl tracking-tight leading-tight">{s.t}</div>
              <div className="mt-3 text-white/60 text-[15px] leading-relaxed">{s.d}</div>
              {i < steps.length - 1 && (
                <ChevronRight className="hidden md:block absolute -right-6 top-1/2 -translate-y-1/2 h-6 w-6 text-white/20" />
              )}
            </motion.div>
          ))}
        </div>
      </div>
    </section>
  );
}

// ============================================================================
// FAQ
// ============================================================================
const FAQS = [
  { q: "How long does implementation take?", a: "Most schools go live within 24 hours of signing. Add your tenant, upload your student roster (or use our CSV import), and every module works immediately." },
  { q: "Can we migrate our existing data from another ERP?", a: "Yes. We support CSV imports for students, staff, fees, and academic records. For SQL-based systems (Sarthak, MyClassCampus, MyEdu), we do the migration for you in 1-2 business days." },
  { q: "Is the platform GDPR/DPDP compliant?", a: "Yes. All data lives in Indian data centres, encrypted at rest and in transit. We follow the DPDP Act 2023 and offer a signed DPA on request." },
  { q: "Do you have a mobile app for parents?", a: "A native React Native app is in beta — currently the web portal is fully mobile-responsive and works on any smartphone browser without installation." },
  { q: "How does per-campus pricing work if we have 20 campuses?", a: "You pay only for active campuses. Onboarding, add-remove, and re-activation are all self-serve — no salespeople involved for scale changes." },
  { q: "Can we host it on our own servers?", a: "Yes — on our enterprise engagement we provide a hardened Docker/Kubernetes bundle with quarterly security patches." },
];

function FAQ() {
  return (
    <section id="faq" className="bg-[#050510] text-white py-24 sm:py-32 border-y border-white/10">
      <div className="max-w-4xl mx-auto px-6">
        <div className="text-[10px] font-mono uppercase tracking-[0.3em] text-blue-400 mb-4 text-center">FAQ</div>
        <h2 className="font-display font-extrabold text-4xl sm:text-5xl tracking-[-0.02em] leading-[0.95] text-center">
          Questions we hear often.
        </h2>
        <Accordion type="single" collapsible className="mt-12">
          {FAQS.map((f, i) => (
            <AccordionItem key={i} value={`f-${i}`} className="border-white/10">
              <AccordionTrigger className="text-left font-display font-semibold text-lg hover:no-underline hover:text-blue-300 py-6" data-testid={`faq-${i}`}>{f.q}</AccordionTrigger>
              <AccordionContent className="text-white/70 text-[15px] leading-relaxed">{f.a}</AccordionContent>
            </AccordionItem>
          ))}
        </Accordion>
      </div>
    </section>
  );
}

// ============================================================================
// CTA
// ============================================================================
function CTA({ onBookDemo }) {
  return (
    <section id="cta" className="bg-[#0a0a10] text-white py-24 sm:py-32">
      <div className="max-w-5xl mx-auto px-6">
        <div className="relative overflow-hidden rounded-3xl border border-blue-500/40 bg-gradient-to-br from-blue-600/25 via-blue-900/40 to-[#050510] p-10 sm:p-16">
          <div className="absolute -top-24 -right-24 h-80 w-80 rounded-full bg-blue-500/30 blur-3xl pointer-events-none" />
          <div className="relative">
            <div className="text-[10px] font-mono uppercase tracking-[0.3em] text-blue-300 mb-4">
              <ShieldCheck className="h-3 w-3 inline -mt-0.5 mr-1.5" /> Ready when you are
            </div>
            <h2 className="font-display font-extrabold text-4xl sm:text-6xl tracking-[-0.03em] leading-[0.95] max-w-3xl">
              See EduKampus running in <span className="text-blue-300">your school</span>.
            </h2>
            <p className="mt-6 text-white/75 text-lg max-w-xl">
              Book a 30-minute walkthrough with our team. We&apos;ll spin up a live sandbox
              branded to your school so you can click around before deciding.
            </p>
            <div className="mt-8 flex flex-col sm:flex-row gap-3">
              <button onClick={onBookDemo} className="inline-flex items-center justify-center gap-2 h-12 px-7 rounded-full bg-white text-[#050510] font-semibold hover:bg-white/90 transition-colors group" data-testid="cta-book-demo">
                Book a demo <ArrowRight className="h-4 w-4 transition-transform group-hover:translate-x-0.5" />
              </button>
              <Link to="/campuses" className="inline-flex items-center justify-center gap-2 h-12 px-7 rounded-full border border-white/25 text-white hover:bg-white/[0.06] font-semibold transition-colors" data-testid="cta-see-directory">
                Browse live portals <ArrowUpRight className="h-4 w-4" />
              </Link>
            </div>
          </div>
        </div>
      </div>
    </section>
  );
}

// ============================================================================
// Footer
// ============================================================================
function Footer() {
  return (
    <footer className="bg-[#050510] text-white/60 border-t border-white/10 pt-16 pb-8">
      <div className="max-w-7xl mx-auto px-6 grid grid-cols-2 md:grid-cols-5 gap-8">
        <div className="col-span-2">
          <div className="flex items-center gap-2 font-display font-extrabold text-white text-lg tracking-tight mb-3">
            <div className="h-8 w-8 rounded-lg bg-gradient-to-br from-blue-500 to-blue-700 grid place-items-center">
              <GraduationCap className="h-4 w-4 text-white" />
            </div>
            EduKampus
          </div>
          <div className="text-sm text-white/50 max-w-sm leading-relaxed">
            The Education Operating System. Multi-tenant, multi-campus school management —
            built for institutions who want tech that keeps up with them.
          </div>
        </div>
        <div>
          <div className="text-[10px] uppercase tracking-[0.25em] text-white/40 mb-3">Product</div>
          <ul className="space-y-2 text-sm">
            <li><a href="#modules" className="hover:text-white">Modules</a></li>
            <li><Link to="/campuses" className="hover:text-white">Directory</Link></li>
            <li><Link to="/login" className="hover:text-white">Sign in</Link></li>
          </ul>
        </div>
        <div>
          <div className="text-[10px] uppercase tracking-[0.25em] text-white/40 mb-3">Company</div>
          <ul className="space-y-2 text-sm">
            <li><a href="#" className="hover:text-white">About</a></li>
            <li><a href="mailto:hello@edukampus.com" className="hover:text-white">Contact</a></li>
            <li><a href="#" className="hover:text-white">Careers</a></li>
          </ul>
        </div>
        <div>
          <div className="text-[10px] uppercase tracking-[0.25em] text-white/40 mb-3">Legal</div>
          <ul className="space-y-2 text-sm">
            <li><a href="#" className="hover:text-white">Privacy</a></li>
            <li><a href="#" className="hover:text-white">Terms</a></li>
            <li><a href="#" className="hover:text-white">DPA</a></li>
          </ul>
        </div>
      </div>
      <div className="mt-14 pt-6 border-t border-white/10 max-w-7xl mx-auto px-6 flex flex-col sm:flex-row items-center justify-between gap-3">
        <div className="text-xs font-mono uppercase tracking-[0.25em] text-white/40">
          © {new Date().getFullYear()} EduKampus · Made in India
        </div>
        <div className="text-xs text-white/40">hello@edukampus.com</div>
      </div>
    </footer>
  );
}

// ============================================================================
// Landing composition
// ============================================================================
export default function Landing() {
  const { user, initializing } = useAuth();
  const navigate = useNavigate();
  const [demoOpen, setDemoOpen] = useState(false);

  // Signed-in users get bounced to dashboard unless they opt-in via ?home=1
  useEffect(() => {
    if (initializing) return;
    const params = new URLSearchParams(window.location.search);
    if (user && !params.get("home")) navigate("/dashboard", { replace: true });
  }, [user, initializing, navigate]);

  useEffect(() => { document.title = "Kampus 365 — Run your entire school on one platform"; }, []);

  // Lenis smooth scroll — scoped to landing, torn down on unmount.
  useEffect(() => {
    const lenis = new Lenis({
      duration: 1.15,
      easing: (t) => Math.min(1, 1.001 - Math.pow(2, -10 * t)),
      smoothWheel: true,
      wheelMultiplier: 1,
      touchMultiplier: 1.4,
    });
    let raf;
    const loop = (time) => { lenis.raf(time); raf = requestAnimationFrame(loop); };
    raf = requestAnimationFrame(loop);
    // Route in-page anchor clicks through Lenis so nav links glide.
    const onAnchorClick = (e) => {
      const a = e.target.closest("a[href^='#']");
      if (!a) return;
      const id = a.getAttribute("href").slice(1);
      if (!id) return;
      const el = document.getElementById(id);
      if (!el) return;
      e.preventDefault();
      lenis.scrollTo(el, { offset: -70 });
    };
    document.addEventListener("click", onAnchorClick);
    return () => {
      document.removeEventListener("click", onAnchorClick);
      cancelAnimationFrame(raf);
      lenis.destroy();
    };
  }, []);

  const openDemo = () => setDemoOpen(true);

  return (
    <div className="min-h-screen bg-[#050510] text-white font-sans" data-testid="landing-page">
      <ScrollProgress color="#3b82f6" />
      <Nav onBookDemo={openDemo} />
      <Hero onBookDemo={openDemo} />
      <TrustBar />
      <Modules />
      <HowItWorks />
      <FAQ />
      <CTA onBookDemo={openDemo} />
      <Footer />
      <BookDemoDialog open={demoOpen} onOpenChange={setDemoOpen} />
    </div>
  );
}
